// Service and location data for SEO pages

export const services = {
  'pipe-fence-installation': {
    title: 'Pipe Fence Installation',
    slug: 'pipe-fence-installation',
    description: 'Professional pipe fence installation using heavy-duty drill pipe or new pipe, designed to withstand Texas weather and livestock pressure.',
    keywords: 'pipe fence installation, drill pipe fencing, new pipe fencing, livestock fencing, ranch fencing, pipe fence weatherford, pipe fence springtown, pipe fence granbury, pipe fence decatur, pipe fence parker county',
    image: '/images/pipe-fence-installation.jpg',
    longDescription: `Pipe fence installation is the gold standard for Texas ranches and properties requiring superior strength and longevity. We offer two pipe material options: drill pipe — the same grade used in oil field operations — and new pipe for property owners who prefer brand-new material. Both options ensure your fence withstands the harshest North Texas conditions. Unlike traditional wooden or wire fencing, pipe fencing provides unmatched durability against livestock pressure, extreme weather, and everyday wear.`,
    benefits: [
      'Heavy-duty drill pipe or new pipe construction',
      'Built to withstand harsh Texas weather',
      'Excellent livestock containment',
      'Minimal maintenance required',
      'Long-lasting durability (30+ years)',
      'Clean, professional installation',
      'Resists rust and corrosion',
      'Increases property value'
    ],
    materials: {
      title: 'Materials We Use',
      description: 'Quality materials are the foundation of a lasting fence.',
      items: [
        {
          name: 'Drill Pipe or New Pipe',
          description: 'Choose drill pipe (oil field grade, excellent value) or new pipe (brand-new steel with a clean, consistent finish). Both deliver superior strength and durability.'
        },
        {
          name: 'Structural Steel Posts',
          description: 'Deep-set steel posts spaced 8-10 feet apart, set in concrete for maximum stability.'
        },
        {
          name: 'Galvanized Components',
          description: 'All hardware and fasteners are galvanized to prevent rust and extend fence lifespan.'
        },
        {
          name: 'High-Tensile Wire (Optional)',
          description: 'Can be combined with wire for additional livestock control while maintaining pipe strength.'
        }
      ]
    },
    pricingFactors: {
      title: 'What Affects Pipe Fence Installation Cost?',
      factors: [
        'Linear footage needed (most projects: 500-2000 feet)',
        'Terrain difficulty and accessibility',
        'Number of gates and entry points',
        'Soil conditions (rocky vs sandy)',
        'Property clearing requirements',
        'Post spacing and height specifications',
        'Additional features (wire, top rail, etc.)'
      ],
      note: 'Average cost: $15-35 per linear foot installed. Contact us for an accurate quote based on your specific property.'
    },
    projectProcess: {
      title: 'Our Installation Process',
      steps: [
        {
          step: 1,
          title: 'Free Site Consultation',
          description: 'We visit your property to assess terrain, measure accurately, and discuss your specific needs.',
          timeline: '1-2 hours on-site'
        },
        {
          step: 2,
          title: 'Detailed Quote & Planning',
          description: 'Receive itemized quote with materials, labor, and timeline. We help with any permit requirements.',
          timeline: '2-3 business days'
        },
        {
          step: 3,
          title: 'Site Preparation',
          description: 'Clear fence line, mark post locations, and prepare for installation.',
          timeline: '1-2 days'
        },
        {
          step: 4,
          title: 'Post Installation',
          description: 'Set steel posts in concrete at proper depth and spacing for your terrain.',
          timeline: '2-4 days'
        },
        {
          step: 5,
          title: 'Pipe & Rail Installation',
          description: 'Weld or bolt pipe rails to posts with precision alignment.',
          timeline: '3-5 days'
        },
        {
          step: 6,
          title: 'Final Inspection',
          description: 'Walk-through with you to ensure everything meets specifications.',
          timeline: '1 day'
        }
      ],
      totalTimeline: 'Typical project: 2-3 weeks from start to finish'
    },
    permitting: {
      title: 'Permits & Regulations',
      content: `In most North Texas counties, agricultural fencing typically doesn't require permits. However, regulations vary by location:
      
**Parker County**: Agricultural fencing generally exempt, but check setback requirements from property lines (usually 3-5 feet).

**Wise County**: Similar agricultural exemptions. Verify with county office for properties near roadways.

**Hood County**: May require survey confirmation for fence placement. We can guide you through the process.

**HOA Properties**: If your property has an HOA, written approval may be required before installation.

We assist with all permit research and can coordinate with local authorities to ensure compliance.`
    },
    faqs: [
      {
        question: 'How long does pipe fence installation take?',
        answer: 'Installation time varies based on property size and terrain. Typically, we can install 500-1000 feet per week. Most residential projects (1000-1500 feet) take 2-3 weeks from start to finish. Larger ranch projects may take 4-6 weeks.'
      },
      {
        question: 'What pipe options do you offer?',
        answer: 'We offer two options: drill pipe and new pipe. Drill pipe is heavy-gauge steel proven in oil field applications — it provides excellent strength at a great value. New pipe is brand-new steel with a clean, consistent finish. Both are available in 2-3/8" and other sizes depending on your project needs.'
      },
      {
        question: 'Do you offer warranties on pipe fence installation?',
        answer: 'Yes, we stand behind our work with a 5-year warranty on workmanship and a 10-year warranty on materials. Pipe fencing typically lasts 30+ years with minimal maintenance.'
      },
      {
        question: 'Can pipe fencing be installed on hilly or rocky terrain?',
        answer: 'Absolutely. Pipe fencing is ideal for challenging Texas terrain. We adjust post depths and spacing to accommodate slopes, and our equipment can handle rocky soil conditions common in Parker and Wise counties.'
      },
      {
        question: 'How deep are the fence posts set?',
        answer: 'Posts are typically set 3-4 feet deep in concrete. For corner and gate posts, we go deeper (4-5 feet) and use larger diameter posts for additional strength.'
      },
      {
        question: 'What maintenance does pipe fencing require?',
        answer: 'Minimal maintenance is one of pipe fencing\'s biggest advantages. Annual inspection for loose connections and occasional touch-up paint on welded areas is typically all that\'s needed. No rotting, warping, or termite issues like wood fencing.'
      },
      {
        question: 'Can you match existing fence height and style?',
        answer: 'Yes, we can match or modify existing fence specifications. Common heights are 4, 5, or 6 feet depending on livestock type and property needs.'
      },
      {
        question: 'Do you remove old fencing?',
        answer: 'Yes, we offer old fence removal as an additional service. We can remove and properly dispose of wood, wire, or damaged pipe fencing before installing your new fence.'
      }
    ]
  },
  'ranch-fence': {
    title: 'Ranch Fence',
    slug: 'ranch-fence',
    description: 'Complete ranch fencing solutions built strong for Texas land owners, including property boundaries, livestock containment, and perimeter security.',
    keywords: 'ranch fence, ranch fencing, property fence, perimeter fencing, livestock fence',
    image: '/images/no-climb-wire-fencing.jpg',
    longDescription: `Ranch fencing is a critical investment for North Texas property owners, providing livestock containment, property definition, and security. Our ranch fencing solutions combine heavy-duty pipe construction with practical design to meet the demanding needs of working ranches and rural properties. Whether you're fencing 10 acres or 500, we build fencing that withstands Texas weather, livestock pressure, and stands for decades.`,
    benefits: [
      'Complete property boundary solutions',
      'Livestock-safe construction',
      'Weather-resistant materials',
      'Custom height and spacing options',
      'Professional installation team',
      'Free site consultation and quotes',
      'Terrain-adaptive installation',
      'Long-term durability (30+ years)'
    ],
    materials: {
      title: 'Ranch Fence Materials',
      description: 'Quality materials built for demanding ranch conditions.',
      items: [
        {
          name: 'Heavy-Duty Pipe Rails',
          description: 'Drill pipe or new pipe for top and mid rails — your choice of material. Both provide excellent strength and livestock visibility.'
        },
        {
          name: 'Reinforced Corner Posts',
          description: 'Extra-heavy corner and brace posts set deeper with additional concrete for maximum stability.'
        },
        {
          name: 'High-Tensile Wire Options',
          description: 'Can incorporate wire mesh or strands for additional livestock control while maintaining pipe strength.'
        },
        {
          name: 'Quality Hardware',
          description: 'Galvanized clips, bolts, and connections designed for Texas weather conditions.'
        }
      ]
    },
    pricingFactors: {
      title: 'Ranch Fence Installation Costs',
      factors: [
        'Total linear footage (most ranches: 1000-5000+ feet)',
        'Terrain challenges (flat vs hilly, rocky soil)',
        'Number of corners and turns',
        'Gate locations and quantities',
        'Height requirements (4ft, 5ft, or 6ft standard)',
        'Wire addition for livestock control',
        'Property clearing and preparation needs'
      ],
      note: 'Average cost: $15-35 per linear foot installed. Larger projects often qualify for volume pricing. Contact us for a detailed quote based on your ranch.'
    },
    faqs: [
      {
        question: 'How much does ranch fencing cost per acre?',
        answer: 'Cost depends on acreage shape and terrain. A square 10-acre property (about 2,640 feet of fence) typically costs $39,600-92,400 installed at $15-35 per foot. Irregular shapes require more fencing and cost more. We provide free site visits to give accurate estimates.'
      },
      {
        question: 'What\'s the best fence height for cattle?',
        answer: 'For cattle, 4-5 feet is standard. Bulls and larger breeds may need 5-6 feet. We assess your specific livestock during consultation to recommend optimal height.'
      },
      {
        question: 'Can ranch fencing handle horses?',
        answer: 'Yes, we can build horse-safe fencing with smooth pipe rails and optional no-climb wire to prevent hoof entanglement. Proper rail spacing prevents horses from getting legs caught.'
      },
      {
        question: 'Do you install fencing on large properties?',
        answer: 'Absolutely. We regularly fence ranches from 10 to 500+ acres throughout Parker, Wise, and Hood counties. Larger projects are scheduled in phases if needed.'
      },
      {
        question: 'How long does ranch fence installation take?',
        answer: 'Installation speed varies by size and terrain. We typically complete 500-800 linear feet per week. A 2,000-foot perimeter fence takes about 3-4 weeks from start to finish.'
      }
    ]
  },
  'custom-gates': {
    title: 'Custom Gates',
    slug: 'custom-gates',
    description: 'Custom-fabricated ranch gates and entry gates designed specifically for your property, built with quality materials and expert welding.',
    keywords: 'custom gates, ranch gates, entry gates, custom gate fabrication, automatic gates, custom gates weatherford, custom gates springtown, ranch gates parker county',
    image: '/images/welding-services.jpg',
    longDescription: `Custom gate fabrication is where functionality meets craftsmanship. Whether you need a grand entrance gate for your ranch, a secure livestock gate, or a decorative entry gate for your property, we design and build gates tailored to your exact specifications. Our custom gates are engineered for smooth operation, lasting durability, and can accommodate automatic openers for modern convenience.`,
    benefits: [
      'Custom-designed for your property',
      'Heavy-duty steel construction',
      'Smooth operation guaranteed',
      'Available in 4-30+ foot widths',
      'Compatible with automatic openers',
      'Matches your existing fencing',
      'Decorative or functional designs',
      'Professional installation included'
    ],
    materials: {
      title: 'Gate Construction Materials',
      description: 'We use premium materials for gates that last generations.',
      items: [
        {
          name: 'Heavy-Gauge Steel Tubing',
          description: '2"x4", 2"x6", or custom sizing based on gate span and weight requirements.'
        },
        {
          name: 'Commercial-Grade Hinges',
          description: 'Adjustable, heavy-duty hinges rated for your gate weight with grease fittings for long-term smooth operation.'
        },
        {
          name: 'Locking Mechanisms',
          description: 'Chain locks, slide bolts, drop rods, or automatic locks compatible with electric openers.'
        },
        {
          name: 'Powder Coat or Paint',
          description: 'Professional finish in your choice of colors to match property aesthetics and prevent rust.'
        }
      ]
    },
    pricingFactors: {
      title: 'Custom Gate Pricing Considerations',
      factors: [
        'Gate width and height (4ft to 30+ ft spans)',
        'Single vs double gate configuration',
        'Design complexity (simple vs ornamental)',
        'Material thickness and weight capacity',
        'Automatic opener integration',
        'Special features (Texas star, ranch name, etc.)',
        'Installation difficulty and post requirements'
      ],
      note: 'Average cost: $800-3,500 per gate depending on size and features. Automatic opener systems add $1,200-2,500.'
    },
    projectProcess: {
      title: 'Custom Gate Design & Installation',
      steps: [
        {
          step: 1,
          title: 'Design Consultation',
          description: 'Discuss your needs, measure opening, review design options and any special requirements.',
          timeline: '1 consultation visit'
        },
        {
          step: 2,
          title: 'Design & Quote',
          description: 'We create detailed drawings and provide itemized quote including materials, fabrication, and installation.',
          timeline: '3-5 business days'
        },
        {
          step: 3,
          title: 'Fabrication',
          description: 'Custom build your gate in our shop with precision welding and professional finishing.',
          timeline: '1-2 weeks'
        },
        {
          step: 4,
          title: 'Site Preparation',
          description: 'Install or reinforce gate posts with proper depth and concrete anchoring.',
          timeline: '1-2 days'
        },
        {
          step: 5,
          title: 'Gate Installation',
          description: 'Mount gate with commercial hinges, install locks, adjust for perfect operation.',
          timeline: '1 day'
        },
        {
          step: 6,
          title: 'Opener Installation (Optional)',
          description: 'Install and program automatic gate opener system if requested.',
          timeline: '1 day'
        }
      ],
      totalTimeline: 'Typical custom gate project: 2-3 weeks from consultation to installation'
    },
    permitting: {
      title: 'Gate Permits & HOA Approval',
      content: `Most agricultural gates don't require permits in North Texas counties. However, certain situations may need attention:

**Entrance Gates Near Public Roads**: Gates opening onto county or state roads may require Texas Department of Transportation (TxDOT) setback compliance. Typically 14-20 feet from road edge.

**Automatic Gate Safety**: Electric gate openers should include safety features like photo eyes and warning signs per industry standards.

**HOA Properties**: Subdivisions and planned communities often have design restrictions. We help navigate approval processes.

**Historic Districts**: Properties in designated historic areas may have additional design requirements.

We review your specific situation and handle any necessary paperwork or coordination with authorities.`
    },
    faqs: [
      {
        question: 'How wide can you make custom gates?',
        answer: 'We build gates from 4 feet to 30+ feet wide. For openings wider than 16 feet, we typically recommend double gates for easier operation and better longevity. Single gates over 20 feet require special engineering.'
      },
      {
        question: 'Can you install automatic gate openers?',
        answer: 'Yes, we design gates compatible with automatic openers and can install complete systems including solar-powered options for remote locations. We work with reliable brands like LiftMaster, Mighty Mule, and Apollo.'
      },
      {
        question: 'What\'s the difference between a ranch gate and an entry gate?',
        answer: 'Ranch gates are typically functional designs prioritizing strength for livestock containment. Entry gates are often more decorative, serving as property landmarks. We build both and can incorporate decorative elements into functional ranch gates.'
      },
      {
        question: 'How long do custom gates last?',
        answer: 'With proper installation and minimal maintenance, our custom steel gates last 25-40 years. Key factors are quality hinges, proper post installation, and occasional lubrication.'
      },
      {
        question: 'Do you do gate repairs on existing gates?',
        answer: 'Absolutely. We repair sagging gates, replace worn hinges, fix locking mechanisms, and can retrofit older gates for automatic opener compatibility.'
      },
      {
        question: 'Can you add custom designs or ranch brands?',
        answer: 'Yes, we can incorporate Texas stars, ranch brands, family names, or custom metalwork into your gate design. This is popular for ranch entrance gates.'
      },
      {
        question: 'What maintenance do custom gates require?',
        answer: 'Minimal maintenance: Annual hinge lubrication, checking bolts for tightness, and occasional touch-up paint if needed. Automatic openers should be serviced every 2-3 years.'
      },
      {
        question: 'How do I know what size gate I need?',
        answer: 'We measure your opening during consultation. Standard guidelines: 10-12 ft for truck access, 14-16 ft for equipment/trailers, 20+ ft for large farm machinery. We recommend adding 2 feet to your widest vehicle/equipment.'
      }
    ],
    gallery: [
      {
        url: '/images/gate-commercial-entrance.jpg',
        alt: 'Commercial Chain-Link Gate Installation',
        title: 'Commercial Gate Entry'
      },
      {
        url: '/images/gate-chainlink-double.jpg',
        alt: 'Double Chain-Link Gate with Blue Frame',
        title: 'Double Gate System'
      },
      {
        url: '/images/gate-commercial-wide.jpg',
        alt: 'Commercial Property Fencing and Gate',
        title: 'Commercial Perimeter Gate'
      },
      {
        type: 'video',
        url: '/images/gate-commercial-video.mp4',
        alt: 'Commercial Gate Installation Video',
        title: 'Gate Installation'
      }
    ]
  },
  'fence-repair': {
    title: 'Fence Repair',
    slug: 'fence-repair',
    description: 'Expert fence repair services for damaged, sagging, or aging fences. Quick repairs to restore functionality and safety across Parker, Wise, and Hood counties.',
    keywords: 'fence repair, fence fix, fence maintenance, damaged fence repair, fence repair weatherford, fence repair springtown, fence repair parker county, sagging fence fix, broken fence post',
    image: '/images/black-pipe-fencing.jpg',
    longDescription: `Fence damage doesn't wait for a convenient time. Whether it's storm damage, vehicle impact, rotting wood posts, or years of livestock pressure, a compromised fence means lost livestock, security risks, and declining property value. North Texas Welding and Fabrication provides fast, professional fence repair services throughout Parker, Wise, and Hood counties. Our mobile welding rigs allow us to reach your property quickly and complete most repairs on-site in a single visit. We repair pipe fences, wire fences, wood fences, and gates of all types—regardless of who originally installed them.`,
    benefits: [
      'Fast response times (24-48 hours)',
      'Structural welding repairs on-site',
      'Post replacement and reinforcement',
      'Wire and panel repairs',
      'Gate hinge, latch, and frame fixes',
      'Storm and impact damage restoration',
      'Cost-effective solutions vs. full replacement',
      'All fence types serviced'
    ],
    materials: {
      title: 'Fence Repair Materials',
      description: 'We carry common repair materials on our service trucks so most jobs can be completed in one visit.',
      items: [
        {
          name: 'Replacement Steel Posts',
          description: 'Matching or upgraded steel posts for damaged or rotted-out posts. Set in concrete for lasting stability.'
        },
        {
          name: 'Pipe Rail Sections',
          description: 'Drill pipe and new pipe in various gauges to match existing fence rail dimensions and strength.'
        },
        {
          name: 'Wire & Mesh Panels',
          description: 'High-tensile wire, no-climb mesh, and welded wire panels for wire fence repairs and reinforcements.'
        },
        {
          name: 'Welding Supplies',
          description: 'Full MIG and stick welding capability on our mobile rigs for structural repairs, re-welding joints, and patching.'
        }
      ]
    },
    pricingFactors: {
      title: 'What Affects Fence Repair Cost?',
      factors: [
        'Type and extent of damage (single post vs. full section)',
        'Fence type (pipe, wire, wood, ornamental)',
        'Number of posts or rails needing replacement',
        'Accessibility of the damaged area',
        'Emergency vs. scheduled repair timing',
        'Materials needed (matching existing fence)',
        'Gate mechanism complexity (for gate repairs)'
      ],
      note: 'Most single-section repairs range from $150-600. Multi-section or structural repairs typically run $500-2,000. We provide free assessments before any work begins.'
    },
    projectProcess: {
      title: 'Our Repair Process',
      steps: [
        {
          step: 1,
          title: 'Damage Assessment',
          description: 'We inspect the full extent of damage, check surrounding sections for hidden issues, and provide an honest recommendation.',
          timeline: 'Same day or next day'
        },
        {
          step: 2,
          title: 'Free Repair Estimate',
          description: 'Receive a clear, itemized quote covering materials, labor, and timeline. No surprises.',
          timeline: 'On-site or within 24 hours'
        },
        {
          step: 3,
          title: 'On-Site Repair',
          description: 'Our crew arrives with materials and mobile welding equipment to complete repairs efficiently.',
          timeline: 'Most repairs: 1 day'
        },
        {
          step: 4,
          title: 'Quality Check',
          description: 'We verify structural integrity, test gates, and confirm the repair meets our standards before leaving.',
          timeline: 'Included with repair'
        }
      ],
      totalTimeline: 'Most fence repairs completed within 1-3 days of first contact'
    },
    faqs: [
      {
        question: 'How quickly can you respond to fence repair needs?',
        answer: 'We typically respond within 24-48 hours for emergency repairs (livestock escape, security breach) and schedule routine repairs within a week. For true emergencies involving livestock safety, call us directly at 817-723-5006.'
      },
      {
        question: 'Do you repair fences installed by other companies?',
        answer: 'Yes, we repair all types of fencing regardless of who installed it originally. We work on pipe fences, wire fences, wood fences, chain link, and ornamental fencing.'
      },
      {
        question: 'Is it better to repair or replace my fence?',
        answer: 'It depends on the extent of damage and the age of the fence. If damage is localized (a few posts or one section), repair is usually more cost-effective. If the fence is over 20 years old with widespread deterioration, replacement may be the better long-term investment. We give you an honest assessment.'
      },
      {
        question: 'Can you match the existing fence style and materials?',
        answer: 'In most cases, yes. We carry common pipe sizes and materials and can source specialty items. For older or unusual fence types, we may need a few extra days to acquire matching materials.'
      },
      {
        question: 'Do you offer emergency fence repair?',
        answer: 'Yes. If you have livestock at risk of escaping or a security concern, call us immediately at 817-723-5006. We prioritize emergency repairs and can often respond same-day.'
      },
      {
        question: 'What causes most fence damage in North Texas?',
        answer: 'The most common causes we see are: severe storms and high winds, fallen trees, vehicle or equipment impact, livestock pressure over time, soil erosion around post bases, and termite/rot damage on wood components.'
      },
      {
        question: 'Do you provide a warranty on fence repairs?',
        answer: 'Yes, we provide a 2-year warranty on all repair workmanship. If any welded joint or replaced component fails under normal conditions, we fix it at no charge.'
      }
    ]
  },
  'mobile-welding': {
    title: 'Mobile Welding Services',
    slug: 'mobile-welding',
    description: 'On-site mobile welding services bringing professional welding equipment directly to your property for convenient repairs and fabrication across North Texas.',
    keywords: 'mobile welding, on-site welding, field welding, portable welding services, mobile welding weatherford, mobile welding springtown, welding services parker county, emergency welding, ranch welding',
    image: '/images/mobile-welding.jpg',
    longDescription: `Not every welding job can come to the shop—and it shouldn't have to. Our mobile welding service brings a fully equipped welding rig directly to your property, ranch, or job site anywhere in North Texas. From emergency fence repairs and broken equipment to on-site fabrication and structural modifications, we deliver the same quality welding you'd get in a controlled shop environment, right where you need it. Our rigs carry MIG, stick, and cutting equipment capable of handling steel, aluminum, and stainless steel projects on location.`,
    benefits: [
      'Come directly to your location',
      'No need to transport heavy equipment',
      'Fully equipped mobile welding rig',
      'MIG, stick, and cutting capabilities',
      'Structural and repair welding',
      'Fast turnaround—most jobs same-day',
      'Emergency service available',
      'Serving Parker, Wise, Hood, and Tarrant counties'
    ],
    materials: {
      title: 'Mobile Welding Equipment & Capabilities',
      description: 'Our mobile rigs are equipped to handle virtually any on-site welding challenge.',
      items: [
        {
          name: 'MIG Welding',
          description: 'Gas metal arc welding for clean, strong joints on steel and aluminum. Ideal for fence repairs, structural work, and fabrication.'
        },
        {
          name: 'Stick Welding (SMAW)',
          description: 'Shielded metal arc welding for heavy structural work, outdoor conditions, and rusty or dirty metals where MIG isn\'t practical.'
        },
        {
          name: 'Plasma & Oxy-Fuel Cutting',
          description: 'Precision cutting equipment for removing damaged sections, cutting pipe to length, and preparing materials on-site.'
        },
        {
          name: 'Grinding & Finishing Tools',
          description: 'Angle grinders, flap discs, and wire wheels for proper prep work and clean finished welds.'
        }
      ]
    },
    pricingFactors: {
      title: 'Mobile Welding Service Pricing',
      factors: [
        'Distance to your location from Springtown',
        'Type and complexity of welding required',
        'Estimated time on-site',
        'Materials needed (provided by us or customer-supplied)',
        'Emergency vs. scheduled appointment',
        'Number of separate weld points or repairs',
        'Metal type (steel, aluminum, stainless)'
      ],
      note: 'Service call starts at $150 (includes travel within 30 miles of Springtown). Hourly rates from $85-125/hr depending on complexity. We provide quotes before starting work.'
    },
    projectProcess: {
      title: 'How Mobile Welding Works',
      steps: [
        {
          step: 1,
          title: 'Call or Request Quote',
          description: 'Describe your project or send photos. We assess scope and provide a ballpark estimate over the phone.',
          timeline: 'Same day response'
        },
        {
          step: 2,
          title: 'Schedule Service',
          description: 'We set a convenient appointment or dispatch for emergencies. You know exactly when we arrive.',
          timeline: 'Usually within 1-3 days'
        },
        {
          step: 3,
          title: 'On-Site Assessment',
          description: 'Our welder inspects the job on arrival, confirms the plan and final pricing before any torch is lit.',
          timeline: '15-30 minutes'
        },
        {
          step: 4,
          title: 'Complete the Work',
          description: 'Professional welding performed on-site with proper safety equipment and quality standards.',
          timeline: 'Most jobs: 1-4 hours'
        },
        {
          step: 5,
          title: 'Inspection & Cleanup',
          description: 'Final quality check, grinding smooth where needed, and cleanup of the work area.',
          timeline: 'Included'
        }
      ],
      totalTimeline: 'Most mobile welding jobs completed in a single visit (2-6 hours on-site)'
    },
    faqs: [
      {
        question: 'What areas do you serve with mobile welding?',
        answer: 'We serve Springtown, Weatherford, Azle, Decatur, Bridgeport, Fort Worth, Granbury, and surrounding communities in Parker, Wise, Hood, and Tarrant counties. Travel fees may apply beyond 30 miles from Springtown.'
      },
      {
        question: 'What types of welding can you do on-site?',
        answer: 'We handle structural welding, equipment repairs, trailer repairs, gate repairs, fence repairs, handrail fabrication, cattle guard repairs, and custom fabrication projects on-site. If it\'s metal and needs welding, we can likely handle it in the field.'
      },
      {
        question: 'Do you provide emergency mobile welding?',
        answer: 'Yes. For urgent situations like broken livestock gates, damaged equipment during work hours, or safety-critical repairs, call us directly at 817-723-5006. We prioritize emergencies and often dispatch same-day.'
      },
      {
        question: 'Is mobile welding as strong as shop welding?',
        answer: 'Absolutely. Our mobile equipment produces the same quality welds as shop equipment. The key difference is environmental control—we take extra precautions in windy or wet conditions to ensure weld quality, and we won\'t weld if conditions compromise the result.'
      },
      {
        question: 'What do I need to prepare before you arrive?',
        answer: 'Clear access to the work area and, if possible, remove any flammable materials within 20 feet. We bring all tools and equipment. If the project requires specific materials, we\'ll discuss that during scheduling.'
      },
      {
        question: 'Can you weld aluminum and stainless steel on-site?',
        answer: 'Yes, our mobile rig is equipped for steel, aluminum, and stainless steel welding. Let us know the material type when requesting a quote so we bring the correct wire and gas.'
      },
      {
        question: 'How much does a mobile welding service call cost?',
        answer: 'Our minimum service call is $150, which includes travel within 30 miles of Springtown and the first hour of work. Additional time is billed at $85-125/hour depending on complexity. We always provide a quote before starting.'
      }
    ]
  },
  'shop-welding': {
    title: 'Shop Welding Services',
    slug: 'shop-welding',
    description: 'Professional shop welding services for custom fabrication projects, structural steel work, and precision welding in a controlled environment near Springtown, TX.',
    keywords: 'shop welding, fabrication welding, structural welding, precision welding, welding shop springtown, welding shop weatherford, custom welding shop, certified welder parker county',
    image: '/images/quality-craftsmanship.jpg',
    longDescription: `Some projects demand the precision and control that only a shop environment can provide. Our welding shop near Springtown is equipped with professional-grade welding, cutting, and fabrication equipment for projects requiring tight tolerances, complex assembly, or specialized finishes. From custom gate fabrication and structural components to equipment modifications and artistic metalwork, our shop handles projects of all sizes with quality craftsmanship and attention to detail. Every project is completed by experienced, certified welders committed to getting it right.`,
    benefits: [
      'Climate-controlled welding environment',
      'Precision welding equipment (MIG, TIG, Stick)',
      'Custom fabrication capabilities',
      'Quality control at every stage',
      'Complex multi-piece assembly',
      'Certified welders on staff',
      'Powder coating and finishing options',
      'Delivery and installation available'
    ],
    materials: {
      title: 'Shop Capabilities & Equipment',
      description: 'Our shop is equipped for a wide range of metal fabrication and welding projects.',
      items: [
        {
          name: 'MIG & TIG Welding',
          description: 'Precision gas-shielded welding for clean, strong joints on steel, aluminum, and stainless steel. TIG for thin materials and cosmetic welds.'
        },
        {
          name: 'Metal Cutting Equipment',
          description: 'Plasma cutter, band saw, and oxy-fuel torch for precise cuts on plate, pipe, tubing, and structural steel.'
        },
        {
          name: 'Bending & Forming',
          description: 'Pipe bender, press brake, and rollers for shaping metal to custom profiles and curves.'
        },
        {
          name: 'Finishing & Coating',
          description: 'Grinding, polishing, primer, paint, and powder coat coordination for a professional, rust-resistant finish on every project.'
        }
      ]
    },
    pricingFactors: {
      title: 'Shop Welding Project Pricing',
      factors: [
        'Project complexity and number of components',
        'Material type and quantity (steel, aluminum, stainless)',
        'Design and engineering time required',
        'Welding method (MIG, TIG, Stick)',
        'Finishing requirements (paint, powder coat, raw)',
        'Delivery and installation needs',
        'Rush vs. standard timeline'
      ],
      note: 'Shop hourly rate: $75-110/hr depending on welding method. Material costs quoted separately. Most custom projects range from $300-5,000+. We provide detailed quotes before beginning any work.'
    },
    projectProcess: {
      title: 'Shop Welding Project Flow',
      steps: [
        {
          step: 1,
          title: 'Project Consultation',
          description: 'Bring your project to the shop or send drawings/photos. We discuss design, materials, timeline, and budget.',
          timeline: 'By appointment'
        },
        {
          step: 2,
          title: 'Design & Quote',
          description: 'We create a detailed plan and itemized quote covering materials, labor, finishing, and delivery if needed.',
          timeline: '2-5 business days'
        },
        {
          step: 3,
          title: 'Material Procurement',
          description: 'Source all materials—steel, hardware, finishing supplies. Customer-supplied materials also welcome.',
          timeline: '1-5 days'
        },
        {
          step: 4,
          title: 'Fabrication & Welding',
          description: 'Cut, fit, weld, and assemble your project with precision in our controlled shop environment.',
          timeline: '1-3 weeks (varies by complexity)'
        },
        {
          step: 5,
          title: 'Finishing & Quality Check',
          description: 'Grind, smooth, and apply finish. Final inspection against specifications before you see the finished product.',
          timeline: '1-3 days'
        },
        {
          step: 6,
          title: 'Pickup or Delivery',
          description: 'Pick up from our shop or we deliver and install. We ensure everything fits and functions perfectly.',
          timeline: 'Scheduled with you'
        }
      ],
      totalTimeline: 'Most shop projects: 1-4 weeks depending on complexity and finishing requirements'
    },
    faqs: [
      {
        question: 'What projects are best for shop welding?',
        answer: 'Custom gates, structural components, equipment modifications, artistic metalwork, precise assemblies, and any project requiring tight tolerances or complex multi-piece construction. If it needs to be perfect, shop welding is the way to go.'
      },
      {
        question: 'Can I visit the shop to discuss my project?',
        answer: 'Yes, we welcome consultations at our shop. Please call 817-723-5006 to schedule an appointment so we can give you our full attention.'
      },
      {
        question: 'What metals can you weld in the shop?',
        answer: 'We weld mild steel, structural steel, stainless steel, aluminum, and chrome-moly. Different metals require different welding processes—we select the right method for the strongest, cleanest result.'
      },
      {
        question: 'Can you work from my drawings or just an idea?',
        answer: 'Both. If you have engineered drawings or CAD files, we can work directly from those. If you have a concept or sketch, we can help develop it into a buildable design. Many of our best projects started as napkin sketches.'
      },
      {
        question: 'Do you offer powder coating?',
        answer: 'We coordinate powder coating through trusted local partners. Powder coat provides a durable, professional finish that outlasts paint and resists chipping, fading, and rust. We handle the logistics so you get a finished product.'
      },
      {
        question: 'What\'s the difference between shop and mobile welding?',
        answer: 'Shop welding offers a controlled environment for precision work, complex assemblies, and projects requiring multiple steps (cutting, bending, welding, finishing). Mobile welding is best for on-site repairs and installations that can\'t be moved to the shop.'
      },
      {
        question: 'Can you repair or modify existing equipment?',
        answer: 'Absolutely. We regularly repair trailers, modify farm equipment, reinforce structures, and adapt machinery. Bring the item to our shop or we can assess it on-site to determine the best approach.'
      }
    ]
  },
  'custom-fabrication': {
    title: 'Custom Fabrication',
    slug: 'custom-fabrication',
    description: 'Custom metal fabrication services for unique projects, structural steel work, and specialized welding in North Texas. From concept to completion.',
    keywords: 'custom fabrication, metal fabrication, structural steel, custom metalwork, steel fabrication weatherford, custom metal springtown, fabrication services parker county, custom steel work',
    image: '/images/quality-craftsmanship.jpg',
    longDescription: `If you can imagine it in metal, we can build it. Our custom fabrication service handles one-of-a-kind projects that don't fit into standard categories—from structural steel supports and custom brackets to ornamental metalwork and agricultural equipment modifications. We work with you from initial concept through design, fabrication, finishing, and installation. Whether you need a single piece or a small production run, our combination of skilled craftsmanship and professional equipment delivers results that match your vision and exceed your expectations.`,
    benefits: [
      'Fully customized to your specifications',
      'Design assistance available',
      'Structural engineering capability',
      'Wide range of metal types and thicknesses',
      'Expert craftsmanship by certified welders',
      'Project consultation included',
      'Finishing and coating options',
      'Delivery and installation services'
    ],
    materials: {
      title: 'Fabrication Capabilities',
      description: 'We work with a wide range of metals and profiles to bring your project to life.',
      items: [
        {
          name: 'Structural Steel',
          description: 'I-beams, channels, angles, and plate for load-bearing structures, supports, and heavy-duty applications.'
        },
        {
          name: 'Pipe & Tubing',
          description: 'Round, square, and rectangular tubing in various sizes for frames, guards, railings, and structural components.'
        },
        {
          name: 'Sheet & Plate Metal',
          description: 'Flat stock from thin gauge sheet to heavy plate for panels, brackets, bases, and custom shapes.'
        },
        {
          name: 'Specialty Metals',
          description: 'Stainless steel, aluminum, and chrome-moly for applications requiring corrosion resistance, light weight, or high strength.'
        }
      ]
    },
    pricingFactors: {
      title: 'Custom Fabrication Pricing',
      factors: [
        'Project complexity and number of pieces',
        'Material type and quantity',
        'Design and engineering time',
        'Welding and assembly hours',
        'Cutting, bending, and forming requirements',
        'Finishing (paint, powder coat, galvanize)',
        'Delivery and installation needs'
      ],
      note: 'Custom fabrication projects typically range from $200 for simple brackets to $10,000+ for complex structural or ornamental work. Every project gets a detailed quote before we begin.'
    },
    projectProcess: {
      title: 'Custom Fabrication Process',
      steps: [
        {
          step: 1,
          title: 'Concept Discussion',
          description: 'Share your idea, drawings, photos, or reference examples. We discuss feasibility, materials, and approach.',
          timeline: 'Phone, email, or in-person'
        },
        {
          step: 2,
          title: 'Design & Engineering',
          description: 'We develop detailed plans, confirm dimensions and specifications, and ensure structural integrity for load-bearing applications.',
          timeline: '3-7 business days'
        },
        {
          step: 3,
          title: 'Quote Approval',
          description: 'Review itemized quote with material costs, labor, finishing, and timeline. No work begins without your approval.',
          timeline: '1-2 business days'
        },
        {
          step: 4,
          title: 'Material Sourcing',
          description: 'Procure all metals, hardware, and supplies. We source from trusted Texas suppliers for quality and availability.',
          timeline: '2-7 days'
        },
        {
          step: 5,
          title: 'Fabrication',
          description: 'Cut, form, weld, and assemble your project. Progress updates provided for larger projects.',
          timeline: '1-4 weeks'
        },
        {
          step: 6,
          title: 'Finishing & Delivery',
          description: 'Apply selected finish, perform quality inspection, and deliver or install the completed piece.',
          timeline: '2-5 days'
        }
      ],
      totalTimeline: 'Simple projects: 1-2 weeks. Complex fabrication: 3-6 weeks. Rush service available for urgent needs.'
    },
    faqs: [
      {
        question: 'What types of custom projects can you handle?',
        answer: 'We handle custom gates, structural supports, agricultural equipment modifications, trailer customizations, railings, cattle guards, brackets, stands, artistic metalwork, signage, and virtually any project involving metal fabrication. If you\'re not sure we can do it, just ask.'
      },
      {
        question: 'Do you provide design assistance?',
        answer: 'Yes, we work with you to design and engineer custom solutions. Whether you have detailed CAD drawings or just a rough sketch and an idea, we can develop it into a buildable plan with accurate dimensions and specifications.'
      },
      {
        question: 'What\'s the minimum project size?',
        answer: 'No project is too small. We fabricate everything from single custom brackets ($200+) to large structural installations ($10,000+). We give every project the same attention to quality.'
      },
      {
        question: 'Can you replicate or repair an existing piece?',
        answer: 'Yes. If you have a broken or worn part, we can replicate it from the original or from measurements. We regularly fabricate replacement parts for equipment, trailers, and structures that are no longer available from manufacturers.'
      },
      {
        question: 'Do you work with architects or contractors?',
        answer: 'Absolutely. We regularly collaborate with architects, general contractors, and engineers on commercial and residential projects. We can work from your plans and specs or help develop fabrication drawings from architectural designs.'
      },
      {
        question: 'What finishing options are available?',
        answer: 'We offer primer and paint, powder coating (through our trusted partner), galvanizing for outdoor/corrosion resistance, and raw/natural finish for indoor or customer-finished applications. We recommend powder coat for the best long-term durability.'
      },
      {
        question: 'Can you do small production runs?',
        answer: 'Yes. If you need multiple identical pieces (brackets, stands, guards, etc.), we can set up for efficient production. Volume pricing applies for repeat items. Typical runs: 5-50 pieces.'
      },
      {
        question: 'How do I get started with a custom project?',
        answer: 'Call us at 817-723-5006 or use our contact form. Describe your project, share any drawings or photos, and we\'ll schedule a consultation to discuss the details. The initial consultation is always free.'
      }
    ]
  },
  'farm-ag': {
    title: 'Farm & AG',
    slug: 'farm-ag',
    description: 'Complete farm and agricultural fencing, livestock pens, cattle working facilities, and ranch infrastructure built tough for North Texas operations.',
    keywords: 'farm fencing north texas, agricultural fencing, livestock pens, cattle working facilities, ranch infrastructure, farm fence springtown, ag fencing weatherford, cattle pens parker county, livestock corrals',
    image: '/images/farm-livestock-pens.jpg',
    longDescription: `North Texas Welding and Fabrication provides complete farm and agricultural infrastructure for working ranches and farms across Parker, Wise, Hood, and Tarrant counties. From livestock pens and sorting alleys to cattle working facilities and perimeter fencing, we build the structures that keep your operation running efficiently. Every project is designed for the demands of real agricultural work—heavy-duty construction, proper livestock flow, and durability that stands up to daily use and Texas weather.`,
    benefits: [
      'Complete livestock pen and corral systems',
      'Cattle working facilities and chute setups',
      'Sorting alleys and holding pens',
      'Heavy-duty pipe construction throughout',
      'Designed for proper livestock flow',
      'Ranch perimeter fencing',
      'Custom gate and entry systems',
      'Built to withstand daily agricultural use'
    ],
    materials: {
      title: 'Farm & AG Materials',
      description: 'We use heavy-duty materials built for the demands of working agricultural operations.',
      items: [
        {
          name: 'Heavy-Gauge Pipe',
          description: 'Drill pipe or new pipe in 2-3/8" and 2-7/8" sizes for rails, posts, and structural components that handle livestock pressure daily.'
        },
        {
          name: 'Continuous Fence Panels',
          description: 'Welded pipe panels in standard and custom configurations for pens, alleys, and working areas.'
        },
        {
          name: 'Steel Posts & Bracing',
          description: 'Deep-set posts with concrete and cross-bracing at corners and stress points for maximum stability.'
        },
        {
          name: 'Gates & Latches',
          description: 'Heavy-duty swing gates with livestock-safe latches designed for one-handed operation during cattle work.'
        }
      ]
    },
    pricingFactors: {
      title: 'What Affects Farm & AG Project Cost?',
      factors: [
        'Total pen/corral system size and layout',
        'Number of pens, alleys, and working areas',
        'Gate quantity and sizes',
        'Cattle chute and head gate requirements',
        'Terrain and site preparation needs',
        'Pipe size and construction specifications',
        'Existing infrastructure modifications'
      ],
      note: 'Every farm project is custom-designed to your operation. Contact us for a free on-site consultation and detailed estimate.'
    },
    projectProcess: {
      title: 'Our Farm & AG Build Process',
      steps: [
        {
          step: 1,
          title: 'On-Site Consultation',
          description: 'We visit your property to understand your operation, livestock flow needs, and discuss the best layout for your setup.',
          timeline: '1-2 hours on-site'
        },
        {
          step: 2,
          title: 'Custom Design & Quote',
          description: 'We create a layout plan showing pen sizes, gate locations, working area flow, and provide a detailed estimate.',
          timeline: '3-5 business days'
        },
        {
          step: 3,
          title: 'Site Preparation',
          description: 'Grade and prepare the site, set post locations, and stage materials for efficient construction.',
          timeline: '1-3 days'
        },
        {
          step: 4,
          title: 'Post & Framework',
          description: 'Set all posts in concrete and build the primary framework for pens, alleys, and working areas.',
          timeline: '3-7 days'
        },
        {
          step: 5,
          title: 'Rails, Panels & Gates',
          description: 'Weld rails, hang panels, install gates, and complete all livestock handling components.',
          timeline: '5-10 days'
        },
        {
          step: 6,
          title: 'Final Walkthrough',
          description: 'Walk through the complete setup with you, test all gates, and confirm everything works for your operation.',
          timeline: '1 day'
        }
      ],
      totalTimeline: 'Typical farm project: 3-6 weeks depending on scope and complexity'
    },
    faqs: [
      {
        question: 'What types of farm and AG projects do you build?',
        answer: 'We build livestock pens, sorting alleys, cattle working facilities (with chute and head gate integration), holding pens, horse pens, loading chutes, perimeter fencing, and complete corral systems. If it involves pipe and livestock, we build it.'
      },
      {
        question: 'Can you integrate with existing chute systems?',
        answer: 'Yes. We work with all major chute brands including Tarter, Priefert, and others. We build the alleys, pens, and connections around your existing or new chute setup for optimal cattle flow.'
      },
      {
        question: 'How do you design the livestock flow?',
        answer: 'Good livestock flow is critical. We design systems based on proven cattle handling principles—curved alleys where appropriate, proper pen sizing, strategic gate placement, and working area layouts that reduce stress on animals and make your job easier.'
      },
      {
        question: 'What areas do you serve?',
        answer: 'We serve all of North Texas including Springtown, Weatherford, Azle, Decatur, Bridgeport, Fort Worth, Granbury, and surrounding communities in Parker, Wise, Hood, and Tarrant counties.'
      },
      {
        question: 'How much does a farm pen system cost?',
        answer: 'Cost varies significantly based on the number of pens, total footage, gate count, and complexity. Small pen additions start around $3,000-5,000. Complete working facility setups with sorting alleys and multiple pens typically range from $10,000-30,000+. We provide detailed quotes after an on-site visit.'
      },
      {
        question: 'Do you build horse facilities too?',
        answer: 'Yes. We build horse pens, round pens, arena fencing, and stall dividers. Horse facilities require specific safety considerations like smooth welds, proper rail spacing, and kick-proof construction that we incorporate into every horse project.'
      }
    ],
    gallery: [
      {
        url: '/images/farm-livestock-pens.jpg',
        alt: 'Livestock Pen Corridor with Pipe Fencing',
        title: 'Livestock Sorting Pens'
      },
      {
        url: '/images/farm-pipe-fence-line.jpg',
        alt: 'Agricultural Pipe Fence Line',
        title: 'Farm Pipe Fencing'
      },
      {
        url: '/images/farm-ranch-perimeter.jpg',
        alt: 'Ranch Perimeter Fencing',
        title: 'Ranch Perimeter Fence'
      },
      {
        url: '/images/farm-cattle-chute.jpg',
        alt: 'Cattle Working Facility with Chute',
        title: 'Cattle Working Facility'
      },
      {
        type: 'video',
        url: '/images/farm-enclosure-video.mp4',
        alt: 'Farm Enclosure Construction Video',
        title: 'Enclosure Build'
      },
      {
        type: 'video',
        url: '/images/farm-gate-install-video.mp4',
        alt: 'Farm Gate Installation Video',
        title: 'Gate Installation'
      }
    ]
  },
  'shops-buildings': {
    title: 'Shops & Buildings',
    slug: 'shops-buildings',
    description: 'Custom metal shops, buildings, patio covers, outdoor kitchens, and concrete slab work across North Texas. Built tough for Texas property owners.',
    keywords: 'metal buildings north texas, custom shops springtown, patio covers weatherford, outdoor kitchens texas, concrete slab work, metal building construction, shop building parker county, steel buildings decatur',
    image: '/images/shops-buildings-patio.jpg',
    longDescription: `From custom metal shops and buildings to patio covers, outdoor kitchens, and concrete slab foundations—North Texas Welding and Fabrication delivers complete turnkey construction for property owners across Parker County, Wise County, and surrounding communities. We handle every phase of the project: site preparation, concrete work, steel framing, roofing, and finishing. Whether you need a backyard workshop, equipment storage building, covered patio, or outdoor entertainment space, our team builds it strong and built to last.`,
    benefits: [
      'Complete turnkey construction from ground up',
      'Custom metal shop & building design',
      'Patio covers & outdoor living structures',
      'Outdoor kitchen fabrication & installation',
      'Concrete slab foundations & prep work',
      'Steel framing & structural welding',
      'Built to withstand Texas weather',
      'Serving all of North Texas'
    ],
    materials: {
      title: 'Materials & Construction',
      description: 'We use commercial-grade materials for every structure we build.',
      items: [
        {
          name: 'Structural Steel Framing',
          description: 'Heavy-gauge steel beams and columns engineered for load-bearing integrity and decades of service.'
        },
        {
          name: 'Metal Roofing & Siding',
          description: 'Commercial-grade corrugated metal panels with weather-resistant coatings for superior protection.'
        },
        {
          name: 'Reinforced Concrete',
          description: 'Rebar-reinforced concrete slabs with proper vapor barriers, graded for drainage and long-term stability.'
        },
        {
          name: 'Premium Fasteners & Hardware',
          description: 'Galvanized and stainless steel fasteners rated for structural applications and harsh environments.'
        }
      ]
    },
    pricingFactors: {
      title: 'What Affects Project Cost?',
      factors: [
        'Building size and footprint dimensions',
        'Foundation and concrete slab requirements',
        'Roof style and pitch specifications',
        'Number of doors, windows, and openings',
        'Interior finishing and insulation needs',
        'Site preparation and grading work',
        'Permit and engineering requirements'
      ],
      note: 'Every project is custom-quoted based on your specific needs. Contact us for a free on-site estimate and consultation.'
    },
    projectProcess: {
      title: 'Our Build Process',
      steps: [
        {
          step: 1,
          title: 'Free On-Site Consultation',
          description: 'We visit your property to assess the site, discuss your vision, and take measurements.',
          timeline: '1-2 hours on-site'
        },
        {
          step: 2,
          title: 'Design & Estimate',
          description: 'Receive a detailed plan with dimensions, materials list, and itemized pricing.',
          timeline: '3-5 business days'
        },
        {
          step: 3,
          title: 'Site Preparation & Concrete',
          description: 'Grade the site, set forms and rebar, pour and finish the concrete slab foundation.',
          timeline: '3-7 days'
        },
        {
          step: 4,
          title: 'Steel Framing & Structure',
          description: 'Erect steel columns, beams, and trusses with precision welding and bolted connections.',
          timeline: '5-10 days'
        },
        {
          step: 5,
          title: 'Roofing, Siding & Finishing',
          description: 'Install metal roofing, wall panels, doors, windows, and any custom features.',
          timeline: '3-7 days'
        },
        {
          step: 6,
          title: 'Final Walkthrough',
          description: 'Complete inspection with you to ensure everything meets your expectations.',
          timeline: '1 day'
        }
      ],
      totalTimeline: 'Typical project: 3-6 weeks depending on size and complexity'
    },
    permitting: {
      title: 'Permits & Regulations',
      content: `Building permits are typically required for shops, buildings, and covered structures in North Texas. Requirements vary by jurisdiction:

**Parker County**: Building permits required for structures over 200 sq ft. Setback and height restrictions apply.

**Wise County**: Permits required for permanent structures. Agricultural exemptions may apply for farm buildings.

**Hood County**: Building permits and plan review required. We coordinate with the county building department on your behalf.

**City Limits**: Properties within city limits (Springtown, Weatherford, etc.) have additional zoning and code requirements.

We handle all permit applications, plan submissions, and inspections to keep your project on track and fully compliant.`
    },
    faqs: [
      {
        question: 'What types of shops and buildings do you build?',
        answer: 'We build a wide range of metal structures including equipment storage shops, workshop buildings, hay barns, covered parking structures, patio covers, outdoor kitchens, and custom commercial buildings. Every project is designed and built to your specific requirements.'
      },
      {
        question: 'Do you handle the concrete slab work too?',
        answer: 'Yes! We provide complete turnkey construction including site grading, form setting, rebar installation, concrete pouring, and finishing. Having one contractor handle everything from foundation to roof ensures quality control and saves you time and money.'
      },
      {
        question: 'How long does it take to build a metal shop?',
        answer: 'Timeline depends on size and complexity. A standard 30x40 shop typically takes 3-4 weeks from concrete pour to completion. Larger or more complex buildings may take 5-6 weeks. We provide a detailed timeline with every estimate.'
      },
      {
        question: 'Can you build patio covers and outdoor kitchens?',
        answer: 'Absolutely. We specialize in custom patio covers, pergolas, and outdoor kitchen structures using heavy-gauge steel. These are designed to match your home\'s style while providing durable, weather-resistant outdoor living spaces.'
      },
      {
        question: 'What size buildings can you construct?',
        answer: 'We build structures from small backyard shops (12x20) to large commercial buildings (60x100+). Our capabilities include any size that can be permitted and built on your property. Contact us to discuss your specific project.'
      },
      {
        question: 'Do you offer insulated buildings?',
        answer: 'Yes, we can install spray foam or batt insulation in any building. Insulated shops are ideal for workshop spaces, climate-controlled storage, or buildings with plumbing and electrical.'
      }
    ],
    gallery: [
      {
        url: '/images/shops-buildings-patio.jpg',
        alt: 'Custom Patio Cover with Outdoor Kitchen - North Texas Welding and Fabrication',
        title: 'Custom Patio Cover & Outdoor Kitchen'
      },
      {
        url: '/images/concrete-slab.jpg',
        alt: 'Finished Concrete Slab Foundation for Metal Building',
        title: 'Concrete Slab Foundation'
      },
      {
        url: '/images/rebar-forms.jpg',
        alt: 'Rebar and Form Work for Building Foundation',
        title: 'Foundation Prep - Rebar & Forms'
      },
      {
        url: '/images/outdoor-kitchen-gazebo.jpg',
        alt: 'Outdoor Kitchen Gazebo Structure',
        title: 'Outdoor Kitchen Gazebo'
      },
      {
        url: '/images/metal-building-interior.jpg',
        alt: 'Metal Building Interior - Steel Frame Construction',
        title: 'Metal Building Interior'
      },
      {
        type: 'video',
        url: '/images/metal-building-build.mp4',
        alt: 'Metal Building Construction Progress Video',
        title: 'Shop Build — Start to Finish'
      }
    ]
  },
  'concrete-contractors-fort-worth': {
    title: 'Concrete Contractors Fort Worth',
    slug: 'concrete-contractors-fort-worth',
    description: 'Professional concrete contractors serving Fort Worth, Springtown, Weatherford & North Texas. Driveways, patios, shop slabs, barn floors & more.',
    keywords: 'concrete contractors fort worth, concrete company north texas, concrete services springtown, concrete work weatherford, commercial concrete fort worth, residential concrete parker county',
    image: '/images/concrete-slab.jpg',
    longDescription: `North Texas Welding and Fabrication provides full-service concrete work for residential, agricultural, and commercial properties across Fort Worth, Springtown, Weatherford, and surrounding North Texas communities. From foundation slabs and driveways to decorative patios and barn floors, our crew handles every phase—site prep, forming, rebar, pouring, finishing, and curing. We bring the same craftsmanship and attention to detail to concrete that we bring to our welding and fencing work.`,
    benefits: [
      'Full-service concrete from site prep to finishing',
      'Residential driveways, patios & walkways',
      'Commercial & agricultural shop slabs',
      'Barn floors & livestock facility foundations',
      'RV pads & equipment pads',
      'Decorative & stamped concrete options',
      'Rebar-reinforced for long-term durability',
      'Serving all of North Texas'
    ],
    materials: {
      title: 'Concrete Materials & Methods',
      description: 'We use commercial-grade materials and proven techniques for every pour.',
      items: [
        { name: '4000+ PSI Concrete Mix', description: 'High-strength concrete rated for vehicle traffic, heavy equipment, and Texas freeze-thaw cycles.' },
        { name: 'Rebar Reinforcement', description: '#4 rebar on 18-inch centers with proper chair support for structural integrity in every slab.' },
        { name: 'Wire Mesh Option', description: '6x6 W2.9/W2.9 welded wire mesh for lighter-duty applications like patios and walkways.' },
        { name: 'Vapor Barriers & Base', description: 'Compacted gravel base with 6-mil poly vapor barrier to prevent moisture migration.' }
      ]
    },
    pricingFactors: {
      title: 'What Affects Your Concrete Cost?',
      factors: [
        'Total square footage of the project',
        'Slab thickness (4", 5", or 6")',
        'Reinforcement type (rebar vs wire mesh)',
        'Existing concrete removal required',
        'Finish type (broom, smooth, stamped, decorative)',
        'Site grading and preparation needs',
        'Saw cut patterns and control joints',
        'Color additives or stamped patterns'
      ],
      note: 'Every project is custom-quoted. Contact us for a free on-site estimate. Deposit is 50% with remaining balance due upon concrete pour.'
    },
    projectProcess: {
      title: 'Our Concrete Process',
      steps: [
        { step: 1, title: 'Free On-Site Estimate', description: 'We visit your property, take measurements, and discuss your project needs and finish preferences.', timeline: '1-2 hours' },
        { step: 2, title: 'Detailed Quote', description: 'Receive an itemized quote with dimensions, thickness, reinforcement, finish options, and total pricing.', timeline: '1-2 business days' },
        { step: 3, title: 'Site Preparation', description: 'Grade the site, compact the base, install vapor barrier, and set forms to exact specifications.', timeline: '1-2 days' },
        { step: 4, title: 'Reinforcement', description: 'Install rebar or wire mesh with proper spacing and elevation for maximum structural strength.', timeline: '1 day' },
        { step: 5, title: 'Pour & Finish', description: 'Pour concrete, screed to grade, apply your chosen finish, and cut control joints.', timeline: '1 day' },
        { step: 6, title: 'Curing & Cleanup', description: 'Apply curing compound, strip forms, and clean up. Concrete reaches full strength in 28 days.', timeline: '1-3 days' }
      ],
      totalTimeline: 'Typical project: 1-2 weeks from start to finish'
    },
    faqs: [
      { question: 'What areas do you serve for concrete work?', answer: 'We serve Fort Worth, Springtown, Weatherford, Azle, Decatur, Bridgeport, Granbury, and all surrounding North Texas communities within Parker, Wise, Hood, and Tarrant counties.' },
      { question: 'How thick should my concrete slab be?', answer: 'Standard residential patios and walkways are 4 inches thick. Driveways and RV pads should be 5-6 inches. Shop and barn slabs are typically 5-6 inches with heavier reinforcement for equipment loads.' },
      { question: 'Do you remove old concrete?', answer: 'Yes, we handle complete tear-out and removal of existing concrete. This includes breaking, hauling, and disposal before your new pour.' },
      { question: 'What is the payment structure?', answer: 'We require a 50% deposit to schedule your project and secure materials. The remaining 50% is due upon concrete pour day.' },
      { question: 'How long before I can use my new concrete?', answer: 'Light foot traffic is safe after 24-48 hours. Vehicle traffic should wait 7 days minimum. Full structural strength is reached at 28 days.' },
      { question: 'Do you offer colored or stamped concrete?', answer: 'Yes! We offer integral color additives, stamped patterns (stone, brick, slate, wood plank), and decorative scoring. These are great for patios, pool decks, and entryways.' }
    ],
    gallery: [
      { url: '/images/concrete-slab.jpg', alt: 'Finished Concrete Slab - Fort Worth Area', title: 'Finished Concrete Slab' },
      { url: '/images/rebar-forms.jpg', alt: 'Rebar and Form Work for Concrete Foundation', title: 'Foundation Prep - Rebar & Forms' }
    ]
  },
  'concrete-patios': {
    title: 'Concrete Patios',
    slug: 'concrete-patios',
    description: 'Custom concrete patios in Springtown, Weatherford & North Texas. Broom finish, stamped, decorative options. Free estimates.',
    keywords: 'concrete patios north texas, patio concrete springtown, stamped concrete patio weatherford, backyard patio concrete, outdoor living concrete, patio installation fort worth',
    image: '/images/shops-buildings-patio.jpg',
    longDescription: `Transform your backyard into a beautiful outdoor living space with a custom concrete patio from North Texas Welding and Fabrication. We design and pour patios of any size and shape, from simple broom-finish slabs to stunning stamped and colored designs. Our patios are built on a properly prepared base with reinforcement to handle Texas heat, freeze cycles, and years of family enjoyment.`,
    benefits: ['Custom sizes and shapes to fit your yard', 'Broom, smooth, stamped & decorative finishes', 'Integrated with outdoor kitchens & covers', 'Proper drainage and grading included', 'Color additives & pattern options', 'Built to handle Texas weather extremes', '5-year workmanship warranty', 'Pairs with our patio cover structures'],
    materials: { title: 'Patio Materials', description: 'Premium materials for lasting outdoor spaces.', items: [
      { name: '4000 PSI Concrete', description: 'High-strength mix rated for outdoor exposure and Texas freeze-thaw cycles.' },
      { name: 'Wire Mesh Reinforcement', description: 'Welded wire mesh prevents cracking and maintains slab integrity over time.' },
      { name: 'Stamping Mats & Color', description: 'Professional-grade stamps and integral color for decorative finishes that last.' },
      { name: 'Sealer & Curing Compound', description: 'UV-resistant sealer protects color and finish from sun damage and staining.' }
    ]},
    pricingFactors: { title: 'Patio Cost Factors', factors: ['Total square footage', 'Finish type (broom, smooth, stamped)', 'Color additives or staining', 'Shape complexity', 'Site preparation needs', 'Existing concrete removal', 'Integration with structures (covers, kitchens)', 'Drainage requirements'], note: 'Contact us for a free patio estimate. 50% deposit required, balance due at pour.' },
    projectProcess: { title: 'Patio Installation Process', steps: [
      { step: 1, title: 'Design Consultation', description: 'Discuss size, shape, finish, and integration with your outdoor living plans.', timeline: '1-2 hours' },
      { step: 2, title: 'Site Prep & Forms', description: 'Grade the area, set forms to exact dimensions, and install base material.', timeline: '1 day' },
      { step: 3, title: 'Reinforcement & Pour', description: 'Install mesh, pour concrete, and apply your chosen finish while fresh.', timeline: '1 day' },
      { step: 4, title: 'Finishing & Curing', description: 'Apply stamps/color if selected, cut control joints, and seal. Ready for use in 3-7 days.', timeline: '1-3 days' }
    ], totalTimeline: 'Typical patio: 3-5 days' },
    faqs: [
      { question: 'What patio finishes do you offer?', answer: 'We offer broom finish (textured, slip-resistant), smooth/trowel finish, stamped patterns (flagstone, brick, slate, wood plank), exposed aggregate, and colored concrete with integral pigments.' },
      { question: 'How thick should a patio be?', answer: 'Standard patios are 4 inches thick with wire mesh. If you plan to place heavy items like hot tubs or outdoor kitchens, we recommend 5 inches with rebar.' },
      { question: 'Can you build a patio around my pool?', answer: 'Absolutely. We pour pool decks with proper drainage slopes, non-slip finishes, and expansion joints to protect your pool structure.' },
      { question: 'Can you connect my patio to an outdoor kitchen or cover?', answer: 'Yes! We build custom patio covers and outdoor kitchen structures too. We can pour your patio and build the structure as one integrated project.' }
    ]
  },
  'driveway-replacement': {
    title: 'Driveway Replacement',
    slug: 'driveway-replacement',
    description: 'Concrete driveway replacement & installation in North Texas. Remove old concrete, pour new reinforced driveways. Free estimates.',
    keywords: 'driveway replacement north texas, concrete driveway fort worth, new driveway springtown, driveway concrete weatherford, driveway installation parker county',
    image: '/images/concrete-slab.jpg',
    longDescription: `Is your driveway cracked, sinking, or falling apart? North Texas Welding and Fabrication provides complete driveway replacement services—from tear-out of your old concrete to pouring and finishing a brand new reinforced driveway. We build driveways that handle daily vehicle traffic, Texas heat expansion, and heavy loads without cracking or settling.`,
    benefits: ['Complete tear-out & removal of old concrete', 'Properly compacted base & grading', 'Rebar-reinforced for vehicle loads', '5-6 inch thickness for maximum durability', 'Broom finish or decorative options', 'Proper control joints to prevent cracking', 'Handles heavy trucks & equipment', 'Increases property value'],
    materials: { title: 'Driveway Materials', description: 'Built to handle daily traffic and Texas weather.', items: [
      { name: '4000+ PSI Concrete', description: 'Vehicle-rated concrete mix designed for heavy loads and temperature extremes.' },
      { name: '#4 Rebar Grid', description: 'Rebar on 18-inch centers provides structural reinforcement against cracking under vehicle weight.' },
      { name: 'Compacted Base', description: '4-6 inches of compacted gravel base prevents settling and ensures proper drainage.' },
      { name: 'Control Joints', description: 'Strategically placed saw cuts control where concrete cracks, keeping your driveway looking clean.' }
    ]},
    pricingFactors: { title: 'Driveway Cost Factors', factors: ['Driveway length and width', 'Slab thickness (5" or 6")', 'Old concrete removal needed', 'Base preparation requirements', 'Finish type selected', 'Turnaround or flared sections', 'Apron connection to street', 'Drainage considerations'], note: 'Free driveway estimates. 50% deposit, balance due at pour.' },
    projectProcess: { title: 'Driveway Replacement Process', steps: [
      { step: 1, title: 'Assessment & Quote', description: 'Measure existing driveway, evaluate base conditions, and provide detailed estimate.', timeline: '1-2 hours' },
      { step: 2, title: 'Tear-Out & Removal', description: 'Break up and haul away old concrete. Grade and compact the subbase.', timeline: '1-2 days' },
      { step: 3, title: 'Forms & Rebar', description: 'Set forms to proper grade, install rebar grid with chairs for correct elevation.', timeline: '1 day' },
      { step: 4, title: 'Pour & Finish', description: 'Pour, screed, finish, and cut control joints. Apply curing compound.', timeline: '1 day' }
    ], totalTimeline: 'Typical driveway: 4-7 days' },
    faqs: [
      { question: 'How long does a driveway replacement take?', answer: 'Most residential driveways take 4-7 days from tear-out to completion. You can walk on it after 48 hours and drive on it after 7 days.' },
      { question: 'How thick should a driveway be?', answer: 'We recommend 5 inches minimum for standard vehicles and 6 inches if you park trucks, trailers, or heavy equipment on it.' },
      { question: 'Will you handle the old concrete removal?', answer: 'Yes, we handle everything—breaking, loading, hauling, and disposal of your old concrete is included in our quote.' },
      { question: 'Can you widen my existing driveway?', answer: 'Absolutely. We can add width, extend the length, or add a turnaround pad to your existing driveway.' }
    ]
  },
  'shop-slabs': {
    title: 'Shop Slabs',
    slug: 'shop-slabs',
    description: 'Heavy-duty concrete shop slabs for metal buildings & workshops in North Texas. Rebar reinforced, properly graded. Free estimates.',
    keywords: 'shop slab concrete, metal building slab, workshop foundation north texas, concrete shop floor springtown, shop slab fort worth, building slab parker county',
    image: '/images/rebar-forms.jpg',
    longDescription: `A proper shop slab is the foundation of any quality metal building or workshop. North Texas Welding and Fabrication pours heavy-duty reinforced concrete slabs engineered for shop and building use. Our slabs are designed to support heavy equipment, vehicle lifts, welding tables, and daily workshop abuse. We handle everything from site preparation to the finished slab, ready for your building erection.`,
    benefits: ['5-6 inch reinforced concrete', 'Engineered for heavy equipment loads', 'Proper grading for drainage', 'Anchor bolt placement for steel buildings', 'Vapor barrier & moisture protection', 'Control joints for crack prevention', 'Turnkey from dirt to finished slab', 'Pairs with our metal building services'],
    materials: { title: 'Shop Slab Materials', description: 'Industrial-grade materials for workshop foundations.', items: [
      { name: '4500 PSI Concrete', description: 'Higher-strength mix rated for heavy equipment, vehicle lifts, and industrial use.' },
      { name: '#4 Rebar on 12" Centers', description: 'Tighter rebar spacing provides superior strength for concentrated equipment loads.' },
      { name: 'Thickened Edge', description: 'Perimeter edges thickened to 8-12 inches for building wall support and anchor bolt embedment.' },
      { name: 'Poly Vapor Barrier', description: '10-mil poly sheeting prevents ground moisture from migrating through the slab.' }
    ]},
    pricingFactors: { title: 'Shop Slab Cost Factors', factors: ['Building footprint dimensions', 'Slab thickness requirements', 'Equipment load specifications', 'Anchor bolt placement needs', 'Site grading and fill requirements', 'Plumbing rough-ins needed', 'Thickened edges and footings', 'Floor drain installation'], note: 'Free shop slab estimates. 50% deposit, balance due at pour.' },
    projectProcess: { title: 'Shop Slab Process', steps: [
      { step: 1, title: 'Site Survey & Engineering', description: 'Survey the site, review building plans, and determine slab specs including anchor bolt layout.', timeline: '1-2 days' },
      { step: 2, title: 'Excavation & Base', description: 'Excavate to depth, import and compact gravel base, install vapor barrier.', timeline: '2-3 days' },
      { step: 3, title: 'Forms, Rebar & Plumbing', description: 'Set forms, install rebar grid, place anchor bolts, and rough in any plumbing.', timeline: '1-2 days' },
      { step: 4, title: 'Pour & Finish', description: 'Pour slab, screed, apply broom or smooth finish, cut joints, and cure.', timeline: '1 day' }
    ], totalTimeline: 'Typical shop slab: 1-2 weeks' },
    faqs: [
      { question: 'How thick should a shop slab be?', answer: 'Standard shop slabs are 5 inches. For heavy equipment shops, welding shops, or vehicle lifts, we recommend 6 inches with #4 rebar on 12-inch centers.' },
      { question: 'Can you pour the slab to match my building plans?', answer: 'Absolutely. We work from your building manufacturer plans to ensure anchor bolts, dimensions, and grades match exactly.' },
      { question: 'Do you install floor drains?', answer: 'Yes, we can install floor drains, trench drains, and plumbing rough-ins during the slab pour.' },
      { question: 'Can you build the metal building too?', answer: 'Yes! We offer complete turnkey shop construction—slab, steel building, and finishing. One contractor, one project.' }
    ]
  },
  'barn-slabs': {
    title: 'Barn Slabs',
    slug: 'barn-slabs',
    description: 'Concrete barn slabs & livestock facility foundations in North Texas. Properly graded for drainage, built for agricultural use.',
    keywords: 'barn slab concrete, barn floor north texas, livestock facility concrete, agricultural concrete springtown, barn foundation weatherford, horse barn slab',
    image: '/images/rebar-forms.jpg',
    longDescription: `Agricultural buildings need concrete that can handle livestock, equipment, moisture, and years of hard use. North Texas Welding and Fabrication pours barn slabs, horse facility floors, livestock handling areas, and agricultural building foundations designed specifically for farm and ranch applications. Our barn slabs feature proper drainage grading, reinforcement for equipment loads, and finishes that provide traction for animals.`,
    benefits: ['Designed for livestock & agricultural use', 'Proper drainage grading for wash-down', 'Non-slip broom finish for animal safety', 'Reinforced for tractor & equipment traffic', 'Integrated drain channels available', 'Resistant to moisture and chemicals', 'Custom sizes for any barn layout', 'Serving all North Texas ranches'],
    materials: { title: 'Barn Slab Materials', description: 'Built tough for agricultural environments.', items: [
      { name: '4000 PSI Concrete', description: 'Standard agricultural-grade concrete rated for equipment traffic and livestock use.' },
      { name: 'Rebar or Fiber Reinforcement', description: 'Rebar grid or fiber-reinforced mix depending on load requirements and budget.' },
      { name: 'Graded Base Material', description: 'Compacted limestone base graded for positive drainage away from the structure.' },
      { name: 'Broom Finish Surface', description: 'Textured broom finish provides traction for livestock and prevents slipping when wet.' }
    ]},
    pricingFactors: { title: 'Barn Slab Cost Factors', factors: ['Barn footprint dimensions', 'Slab thickness for expected loads', 'Drainage requirements', 'Number of stalls or sections', 'Equipment access needs', 'Site preparation and grading', 'Plumbing or drain installation', 'Distance from road access'], note: 'Free barn slab estimates. 50% deposit, balance due at pour.' },
    projectProcess: { title: 'Barn Slab Process', steps: [
      { step: 1, title: 'Ranch Visit & Assessment', description: 'Visit your property to assess the site, discuss barn layout, and plan drainage.', timeline: '1-2 hours' },
      { step: 2, title: 'Site Prep & Grading', description: 'Grade the site for proper drainage, compact base material, and install forms.', timeline: '2-3 days' },
      { step: 3, title: 'Reinforcement & Drains', description: 'Install rebar/mesh and any drain channels or plumbing rough-ins.', timeline: '1 day' },
      { step: 4, title: 'Pour & Broom Finish', description: 'Pour slab with proper drainage slopes, apply broom finish for traction, and cure.', timeline: '1 day' }
    ], totalTimeline: 'Typical barn slab: 1-2 weeks' },
    faqs: [
      { question: 'What finish is best for horse barns?', answer: 'Broom finish provides the best traction for horses and livestock. It creates a textured surface that prevents slipping when wet, while still being easy to clean.' },
      { question: 'How do you handle drainage in a barn slab?', answer: 'We grade the slab with a slight slope (typically 1/8" per foot) toward drain channels or the barn opening. This allows wash-down water to flow out naturally.' },
      { question: 'Can you pour a slab for an existing barn?', answer: 'Yes, we can pour concrete inside existing structures. We work around posts, walls, and other obstacles to give you a solid, level floor.' },
      { question: 'Do barn slabs need rebar?', answer: 'For barns with heavy equipment traffic (tractors, skid steers), yes. For lighter-use horse stalls, fiber-reinforced concrete or wire mesh may be sufficient and more cost-effective.' }
    ]
  },
  'decorative-concrete': {
    title: 'Decorative Concrete',
    slug: 'decorative-concrete',
    description: 'Decorative concrete services in North Texas. Colored, scored, exposed aggregate & custom finishes for patios, driveways & pool decks.',
    keywords: 'decorative concrete north texas, colored concrete fort worth, exposed aggregate springtown, decorative concrete patio weatherford, custom concrete finishes, concrete design parker county',
    image: '/images/shops-buildings-patio.jpg',
    longDescription: `Decorative concrete transforms ordinary slabs into stunning design features. North Texas Welding and Fabrication offers a full range of decorative concrete options including integral color, acid staining, scored patterns, exposed aggregate, and custom border designs. Perfect for patios, pool decks, driveways, entryways, and outdoor living areas that deserve more than a basic gray finish.`,
    benefits: ['Integral color additives (dozens of shades)', 'Acid staining for unique patterns', 'Scored & saw-cut designs', 'Exposed aggregate finishes', 'Custom border & band designs', 'UV-resistant sealers included', 'Combines beauty with durability', 'Ideal for patios, driveways & pool decks'],
    materials: { title: 'Decorative Materials', description: 'Premium decorative materials for distinctive results.', items: [
      { name: 'Integral Color Pigments', description: 'Iron oxide pigments mixed directly into the concrete for color that goes all the way through—won\'t chip or fade.' },
      { name: 'Acid Stains', description: 'Chemical stains that react with the concrete to create unique, one-of-a-kind color variations and patterns.' },
      { name: 'Decorative Sealers', description: 'High-gloss or matte UV-resistant sealers that protect color and enhance the finished appearance.' },
      { name: 'Scoring Tools', description: 'Diamond blade tools for precise geometric patterns, borders, and custom designs cut into the surface.' }
    ]},
    pricingFactors: { title: 'Decorative Concrete Cost Factors', factors: ['Total square footage', 'Number of colors used', 'Pattern complexity', 'Scoring or border designs', 'Sealer type (matte vs high-gloss)', 'Existing concrete removal', 'Site preparation needs', 'Integration with surrounding surfaces'], note: 'Free decorative concrete estimates. 50% deposit, balance due at pour.' },
    projectProcess: { title: 'Decorative Concrete Process', steps: [
      { step: 1, title: 'Design Consultation', description: 'Review color samples, pattern options, and design possibilities for your space.', timeline: '1-2 hours' },
      { step: 2, title: 'Sample Approval', description: 'We create a small sample with your chosen colors and patterns for approval before the full pour.', timeline: '3-5 days' },
      { step: 3, title: 'Prep & Pour', description: 'Prepare site, set forms, pour concrete with integral color, and apply decorative treatments while fresh.', timeline: '1-2 days' },
      { step: 4, title: 'Seal & Protect', description: 'Apply UV-resistant sealer after curing to protect the color and finish for years to come.', timeline: '2-3 days after pour' }
    ], totalTimeline: 'Typical decorative project: 1-2 weeks' },
    faqs: [
      { question: 'Will the color fade over time?', answer: 'Integral color pigments are extremely durable and UV-resistant. With proper sealing every 2-3 years, your colored concrete will maintain its appearance for decades.' },
      { question: 'Can you add decorative elements to existing concrete?', answer: 'Yes! Acid staining, scoring, and overlay systems can transform existing gray concrete into decorative surfaces without a complete tear-out.' },
      { question: 'What colors are available?', answer: 'We offer dozens of integral color options from earth tones (tans, browns, terracotta) to grays, charcoals, and reds. Custom color matching is also available.' },
      { question: 'Is decorative concrete slippery?', answer: 'Not when finished properly. We apply non-slip additives to sealers and can add broom-textured borders for areas that need extra traction, like pool decks.' }
    ]
  },
  'rv-pads': {
    title: 'RV Pads',
    slug: 'rv-pads',
    description: 'Concrete RV pads & parking slabs in North Texas. Heavy-duty reinforced concrete for motorhomes, trailers & equipment. Free estimates.',
    keywords: 'rv pad concrete north texas, rv parking slab fort worth, concrete rv pad springtown, motorhome pad weatherford, rv pad installation parker county',
    image: '/images/concrete-slab.jpg',
    longDescription: `Keep your RV, motorhome, boat, or trailer on a proper concrete pad instead of sinking into the dirt. North Texas Welding and Fabrication pours heavy-duty RV pads engineered for the weight and size of recreational vehicles. Our pads feature reinforced concrete, proper drainage slopes, and utility hookup provisions so your RV stays level, clean, and ready to roll.`,
    benefits: ['Sized for any RV, motorhome, or trailer', 'Reinforced for heavy vehicle weight', 'Proper drainage slopes included', 'Utility hookup provisions available', 'Level surface prevents RV settling', 'Extends RV and tire lifespan', 'Increases property value', 'Clean, mud-free parking year-round'],
    materials: { title: 'RV Pad Materials', description: 'Built to support heavy recreational vehicles.', items: [
      { name: '4000+ PSI Concrete', description: 'Vehicle-rated concrete designed to support motorhomes, fifth-wheels, and heavy trailers.' },
      { name: 'Rebar Reinforcement', description: '#4 rebar on 18-inch centers prevents cracking under concentrated wheel loads.' },
      { name: 'Compacted Gravel Base', description: '6-inch compacted gravel base prevents settling and ensures long-term stability.' },
      { name: 'Thickened Edges', description: 'Edges thickened to 6-8 inches to resist chipping from vehicle traffic.' }
    ]},
    pricingFactors: { title: 'RV Pad Cost Factors', factors: ['Pad length and width', 'Slab thickness (5" or 6")', 'Site grading requirements', 'Utility hookup provisions', 'Approach apron needed', 'Existing surface removal', 'Drainage solutions', 'Finish type selected'], note: 'Free RV pad estimates. 50% deposit, balance due at pour.' },
    projectProcess: { title: 'RV Pad Installation', steps: [
      { step: 1, title: 'Site Assessment', description: 'Measure your RV, plan pad dimensions, assess grade, and discuss utility needs.', timeline: '1 hour' },
      { step: 2, title: 'Excavation & Base', description: 'Excavate, grade for drainage, compact gravel base, and set forms.', timeline: '1 day' },
      { step: 3, title: 'Rebar & Pour', description: 'Install rebar, pour concrete to specified thickness, and apply broom finish.', timeline: '1 day' },
      { step: 4, title: 'Cure & Use', description: 'Apply curing compound. Light use after 48 hours, RV parking after 7 days.', timeline: '7 days' }
    ], totalTimeline: 'Typical RV pad: 3-5 days' },
    faqs: [
      { question: 'What size RV pad do I need?', answer: 'Standard RV pads are 12x50 feet for most travel trailers and 16x60 feet for large motorhomes. We customize to your specific RV dimensions plus room for awnings and hookups.' },
      { question: 'How thick should an RV pad be?', answer: 'We recommend 5 inches minimum with rebar for standard RVs and 6 inches for large motorhomes, fifth-wheels, or if you also park heavy trucks on it.' },
      { question: 'Can you include utility hookups?', answer: 'We can rough in conduit and plumbing during the pour for electrical, water, and sewer hookups. You would need a licensed electrician and plumber for the final connections.' },
      { question: 'Will an RV pad increase my property value?', answer: 'Yes. A proper RV pad is a desirable feature that adds real value, especially in rural North Texas properties where RV and boat storage is common.' }
    ]
  },
  'stamped-concrete': {
    title: 'Stamped Concrete',
    slug: 'stamped-concrete',
    description: 'Stamped concrete patios, driveways & walkways in North Texas. Stone, brick, slate & wood plank patterns. Professional installation.',
    keywords: 'stamped concrete north texas, stamped patio fort worth, stamped driveway springtown, stamped concrete patterns weatherford, decorative stamped concrete, stamped concrete installation',
    image: '/images/shops-buildings-patio.jpg',
    longDescription: `Stamped concrete delivers the look of natural stone, brick, slate, or wood at a fraction of the cost. North Texas Welding and Fabrication creates stunning stamped concrete surfaces for patios, driveways, walkways, pool decks, and entryways. Our skilled crew works with precision stamping tools and color combinations to create surfaces that are virtually indistinguishable from natural materials—but far more durable and maintenance-free.`,
    benefits: ['Natural stone, brick, slate & wood patterns', 'Integral color + release agent depth', 'Fraction of the cost of natural materials', 'More durable than pavers or natural stone', 'No weeds, shifting, or settling', 'UV-resistant sealed finish', 'Dozens of patterns and color combos', 'Perfect for patios, driveways & pool decks'],
    materials: { title: 'Stamped Concrete Materials', description: 'Professional-grade stamping materials for authentic results.', items: [
      { name: 'Professional Stamp Mats', description: 'Heavy-duty texture mats in patterns like ashlar slate, cobblestone, flagstone, herringbone brick, and wood plank.' },
      { name: 'Integral Base Color', description: 'Color mixed into the concrete provides the primary tone—available in earth tones, grays, and custom colors.' },
      { name: 'Powder Release Agent', description: 'Antiquing release agent creates contrast and depth between the stamp impressions for a natural, aged look.' },
      { name: 'Acrylic Sealer', description: 'High-quality acrylic sealer enhances color, adds sheen, and protects the surface from UV and wear.' }
    ]},
    pricingFactors: { title: 'Stamped Concrete Cost Factors', factors: ['Total square footage', 'Pattern selected', 'Number of colors', 'Border or accent bands', 'Site preparation needs', 'Old concrete removal', 'Shape complexity', 'Sealer type (matte vs wet-look)'], note: 'Free stamped concrete estimates. 50% deposit, balance due at pour.' },
    projectProcess: { title: 'Stamped Concrete Process', steps: [
      { step: 1, title: 'Pattern & Color Selection', description: 'Review our pattern book and color samples. See examples of finished work to choose your combination.', timeline: '1-2 hours' },
      { step: 2, title: 'Site Preparation', description: 'Grade site, set forms, install reinforcement and base material.', timeline: '1-2 days' },
      { step: 3, title: 'Pour & Stamp', description: 'Pour colored concrete, apply release agent, and press stamp patterns while concrete is fresh. Timing is critical.', timeline: '1 day' },
      { step: 4, title: 'Wash, Detail & Seal', description: 'Wash off release agent to reveal color contrast, detail edges, and apply protective sealer.', timeline: '2-3 days after pour' }
    ], totalTimeline: 'Typical stamped project: 1-2 weeks' },
    faqs: [
      { question: 'What patterns are available?', answer: 'Popular patterns include ashlar slate, random flagstone, cobblestone, herringbone brick, Roman slate, and wood plank. We carry over 20 patterns and can show you samples.' },
      { question: 'How does stamped concrete compare to pavers?', answer: 'Stamped concrete is typically 30-50% less expensive than natural stone pavers, requires no maintenance (no weeds, no re-leveling), and is a single solid slab that won\'t shift or settle.' },
      { question: 'Does stamped concrete crack?', answer: 'Properly installed stamped concrete with reinforcement and control joints rarely develops structural cracks. Hairline surface cracks can occur but are typically hidden by the pattern texture.' },
      { question: 'How often does stamped concrete need resealing?', answer: 'We recommend resealing every 2-3 years to maintain the color and finish. It is a simple DIY project or we can do it for you as a maintenance service.' }
    ]
  },

  // ─────────────────────────────────────
  // GEOGRAPHIC SERVICE HUBS
  // ─────────────────────────────────────

  'parker-county-texas': {
    title: 'Parker County, Texas',
    slug: 'parker-county-texas',
    isGeoHub: true,
    description: 'Full-service welding, fencing, concrete, metal buildings, and land services throughout Parker County, TX. Serving Weatherford, Springtown, Aledo, Willow Park, and Hudson Oaks.',
    keywords: 'Parker County welding, Parker County fencing, Weatherford fence, Springtown welding, Parker County concrete, Parker County metal buildings',
    image: '/images/hero-fence.jpg',
    longDescription: 'North Texas Welding and Fabrication is Parker County\'s trusted contractor for pipe fencing, land clearing, metal buildings, concrete, custom gates, and welding services. Headquartered in Springtown and serving the entire county, we bring professional craftsmanship and heavy-duty equipment to residential, agricultural, and commercial projects across Weatherford, Aledo, Willow Park, Hudson Oaks, and all surrounding Parker County communities.',
    benefits: [
      'Pipe fence installation for ranches and homes',
      'Land clearing and forestry mulching',
      'Custom metal buildings and shop construction',
      'Concrete driveways, slabs, patios, and foundations',
      'Custom gates and automated gate systems',
      'Gravel driveways and dirt work',
      'Mobile welding and on-site repairs',
      'Shop welding and custom fabrication'
    ],
    cities: ['Weatherford', 'Springtown', 'Aledo', 'Willow Park', 'Hudson Oaks'],
    geoHubLinks: [
      { label: 'Springtown Services Hub', url: '/services/springtown-texas' },
      { label: 'Weatherford Services Hub', url: '/services/weatherford-texas' },
      { label: 'Pipe Fence Springtown', url: '/pipe-fence-springtown-tx' },
      { label: 'Pipe Fence Weatherford', url: '/pipe-fence-weatherford-tx' },
      { label: 'Metal Buildings Springtown', url: '/metal-buildings-springtown-tx' },
      { label: 'Metal Buildings Weatherford', url: '/metal-buildings-weatherford-tx' },
      { label: 'Land Clearing Springtown', url: '/land-clearing-springtown-tx' },
      { label: 'Land Clearing Weatherford', url: '/land-clearing-weatherford-tx' },
      { label: 'Concrete Springtown', url: '/concrete-contractor-springtown-tx' },
      { label: 'Concrete Weatherford', url: '/concrete-contractor-weatherford-tx' },
      { label: 'Gravel Driveways Springtown', url: '/gravel-driveways-springtown-tx' },
      { label: 'Dirt Work Springtown', url: '/dirt-work-springtown-tx' },
      { label: 'Custom Gates Springtown', url: '/custom-gates-springtown-tx' }
    ],
    faqs: [
      { question: 'What services do you offer in Parker County?', answer: 'We offer pipe fencing, ranch fencing, custom gates, land clearing, forestry mulching, metal building construction, all types of concrete work (driveways, patios, slabs, barn floors), gravel driveways, dirt work, mobile welding, shop welding, and custom metal fabrication throughout Parker County.' },
      { question: 'What cities in Parker County do you serve?', answer: 'We serve all of Parker County including Weatherford, Springtown, Aledo, Willow Park, Hudson Oaks, Peaster, Poolville, Millsap, Cool, and all surrounding rural communities.' },
      { question: 'Do Parker County projects require permits?', answer: 'It depends on the project. Agricultural fencing typically does not require permits. Metal buildings, concrete foundations, and covered structures may need building permits from Parker County or from the city if within city limits. We help determine permit requirements and handle the paperwork for you.' },
      { question: 'Where is your shop located?', answer: 'Our shop is in Springtown, Texas — centrally located in Parker County. We are a short drive from Weatherford, Azle, and most Parker County communities.' }
    ]
  },

  'springtown-texas': {
    title: 'Springtown, Texas',
    slug: 'springtown-texas',
    isGeoHub: true,
    description: 'Local welding, pipe fence, concrete, metal building, and land clearing services in Springtown, TX. Headquartered here — serving residential, ranch, and commercial properties.',
    keywords: 'Springtown welding, Springtown fence installation, Springtown pipe fence, Springtown concrete, Springtown metal buildings, Springtown land clearing',
    image: '/images/hero-fence.jpg',
    longDescription: 'North Texas Welding and Fabrication is headquartered right here in Springtown, Texas. As your local contractor, we provide the full range of services that Springtown property owners need: pipe fencing for ranches and acreage homes, land clearing for new construction and pasture restoration, metal buildings for workshops and barns, complete concrete services, custom gates, and professional welding. Being based in Springtown means faster response times, lower travel costs, and a contractor who knows the local terrain, soil conditions, and county regulations firsthand.',
    benefits: [
      'Headquartered in Springtown — your local contractor',
      'Pipe fence installation for ranches and residential properties',
      'Land clearing and forestry mulching',
      'Metal building and shop construction',
      'Concrete patios, driveways, slabs, and foundations',
      'Custom gates and automated gate systems',
      'Gravel driveways and site grading',
      'Mobile and shop welding services'
    ],
    cities: ['Springtown'],
    geoHubLinks: [
      { label: 'Pipe Fence Springtown', url: '/pipe-fence-springtown-tx' },
      { label: 'Metal Buildings Springtown', url: '/metal-buildings-springtown-tx' },
      { label: 'Land Clearing Springtown', url: '/land-clearing-springtown-tx' },
      { label: 'Concrete Contractor Springtown', url: '/concrete-contractor-springtown-tx' },
      { label: 'Gravel Driveways Springtown', url: '/gravel-driveways-springtown-tx' },
      { label: 'Dirt Work Springtown', url: '/dirt-work-springtown-tx' },
      { label: 'Custom Gates Springtown', url: '/custom-gates-springtown-tx' }
    ],
    faqs: [
      { question: 'Are you actually based in Springtown?', answer: 'Yes. Our shop and business are headquartered in Springtown, TX. We are not a distant company sending crews — we live and work here.' },
      { question: 'What services do you offer in Springtown?', answer: 'We offer pipe fencing, ranch fencing, custom gates, land clearing, forestry mulching, metal building construction, concrete work (driveways, patios, slabs, barn floors), gravel driveways, dirt work, mobile welding, and custom metal fabrication.' },
      { question: 'Do you serve areas outside Springtown?', answer: 'Absolutely. We serve all of Parker County, Wise County, Hood County, and surrounding North Texas communities including Weatherford, Azle, Decatur, Granbury, Bridgeport, and more.' },
      { question: 'How quickly can you start a project in Springtown?', answer: 'Being local means we can often do a site consultation within 1-2 business days and begin work within 1-2 weeks of signing, depending on project size and current schedule.' }
    ]
  },

  'weatherford-texas': {
    title: 'Weatherford, Texas',
    slug: 'weatherford-texas',
    isGeoHub: true,
    description: 'Expert fencing, welding, concrete, and metal building services in Weatherford, TX. Custom gates, pipe fencing, land clearing, and more for the Parker County seat.',
    keywords: 'Weatherford welding, Weatherford fencing, Weatherford custom gates, Weatherford fence repair, Weatherford concrete, Weatherford metal buildings',
    image: '/images/hero-fence.jpg',
    longDescription: 'Weatherford, Texas — the Parker County seat — is one of North Texas Welding and Fabrication\'s primary service areas. From historic ranch properties to new acreage developments on the outskirts of town, Weatherford property owners count on us for professional pipe fencing, land clearing, metal buildings, concrete work, custom gates, and welding services. Our Springtown shop is just a short drive from Weatherford, which means fast response and competitive pricing for every project.',
    benefits: [
      'Short drive from our Springtown shop',
      'Pipe fence installation for ranches and acreage',
      'Land clearing and brush removal',
      'Metal building and barn construction',
      'Concrete driveways, patios, and slabs',
      'Custom entry and ranch gates',
      'Equestrian facility construction',
      'Mobile welding for on-site repairs'
    ],
    cities: ['Weatherford'],
    geoHubLinks: [
      { label: 'Pipe Fence Weatherford', url: '/pipe-fence-weatherford-tx' },
      { label: 'Metal Buildings Weatherford', url: '/metal-buildings-weatherford-tx' },
      { label: 'Land Clearing Weatherford', url: '/land-clearing-weatherford-tx' },
      { label: 'Concrete Contractor Weatherford', url: '/concrete-contractor-weatherford-tx' }
    ],
    faqs: [
      { question: 'What services do you offer in Weatherford?', answer: 'We offer pipe fencing, ranch fencing, custom gates, land clearing, forestry mulching, metal building construction, concrete work (driveways, patios, shop slabs, barn floors), mobile welding, shop welding, and custom fabrication in Weatherford and the surrounding area.' },
      { question: 'How far are you from Weatherford?', answer: 'Our shop in Springtown is approximately 15-20 minutes from Weatherford. We serve Weatherford regularly and have completed numerous projects throughout the city and surrounding rural areas.' },
      { question: 'Do you work with Weatherford equestrian properties?', answer: 'Yes. Weatherford is known for its equestrian community, and we build horse-safe pipe fencing, arenas, round pens, paddock systems, and barn facilities for equestrian properties throughout the area.' },
      { question: 'Do I need permits for fencing in Weatherford city limits?', answer: 'Within Weatherford city limits, fencing may require a permit depending on height and location. Outside city limits in unincorporated Parker County, agricultural fencing typically does not need permits. We help determine your specific requirements.' }
    ]
  },

  'granbury-texas': {
    title: 'Granbury, Texas',
    slug: 'granbury-texas',
    isGeoHub: true,
    description: 'Quality welding, fencing, concrete, and metal building services in Granbury, TX. Serving Hood County with professional pipe fencing, custom gates, land clearing, and more.',
    keywords: 'Granbury welding, Granbury fencing, Granbury custom gates, Granbury fence installation, Granbury concrete, Hood County fencing',
    image: '/images/hero-fence.jpg',
    longDescription: 'North Texas Welding and Fabrication serves Granbury, Texas and the greater Hood County area with professional pipe fencing, custom gates, land clearing, metal building construction, concrete work, and welding services. Granbury\'s unique mix of lakefront properties, ranches, and growing residential developments means diverse project needs — and our full-service approach means one contractor can handle it all. From ranch fencing along FM 4 to shop construction on acreage properties, we bring Parker County craftsmanship to Hood County.',
    benefits: [
      'Pipe fence installation for ranches and lakefront properties',
      'Custom gates and automated entry systems',
      'Land clearing and forestry mulching',
      'Metal building and barn construction',
      'Concrete driveways, patios, and slabs',
      'Mobile welding for on-site repairs',
      'Shop welding and custom fabrication',
      'Serving all of Hood County'
    ],
    cities: ['Granbury'],
    faqs: [
      { question: 'Do you serve Granbury regularly?', answer: 'Yes. Granbury and Hood County are part of our regular service area. We complete projects throughout the Granbury area including lakefront properties, rural ranches, and in-town residential.' },
      { question: 'How far is your shop from Granbury?', answer: 'Our Springtown shop is approximately 35-40 minutes from Granbury. We factor travel into our scheduling and frequently have crews working in the Granbury area.' },
      { question: 'What services are most popular in Granbury?', answer: 'Pipe fencing for ranch and acreage properties, custom entry gates, concrete driveways and patios for lakefront homes, and metal building construction are our most common Granbury projects.' },
      { question: 'Do you handle Hood County permits?', answer: 'Yes. We help determine permit requirements for your specific project and location, and assist with applications for metal buildings and other structures requiring Hood County approval.' }
    ]
  },

  'decatur-texas': {
    title: 'Decatur, Texas',
    slug: 'decatur-texas',
    isGeoHub: true,
    description: 'Professional fencing, welding, concrete, and fabrication services in Decatur, TX. Expert pipe fence installation, ranch fencing, and custom metalwork for Wise County.',
    keywords: 'Decatur welding, Decatur fencing, Decatur pipe fence, Decatur ranch fence, Decatur concrete, Wise County welding',
    image: '/images/hero-fence.jpg',
    longDescription: 'North Texas Welding and Fabrication serves Decatur, Texas and the Wise County area with professional pipe fencing, ranch fencing, custom gates, land clearing, concrete work, metal buildings, and welding services. Decatur\'s agricultural heritage and growing residential communities create strong demand for quality fencing and construction. Our team understands the terrain, soil conditions, and regulatory requirements specific to Wise County properties.',
    benefits: [
      'Pipe fence and ranch fencing installation',
      'Custom gates and entry systems',
      'Land clearing and brush removal',
      'Metal building construction',
      'Concrete work for all applications',
      'Mobile welding and emergency repairs',
      'Custom metal fabrication',
      'Farm and agricultural facilities'
    ],
    cities: ['Decatur'],
    faqs: [
      { question: 'Do you serve the Decatur area?', answer: 'Yes. Decatur and Wise County are part of our regular service territory. We complete pipe fencing, ranch fencing, concrete, and metal building projects throughout the area.' },
      { question: 'How far are you from Decatur?', answer: 'Our shop in Springtown is approximately 25-30 minutes from Decatur. We frequently work in the Decatur and Wise County area.' },
      { question: 'What types of projects do you do in Decatur?', answer: 'Ranch fencing, pipe fencing for acreage properties, custom gates, cattle working facilities, concrete slabs and driveways, metal shops and barns, and mobile welding are the most common projects we complete in the Decatur area.' },
      { question: 'Do you serve other Wise County communities?', answer: 'Yes. Beyond Decatur, we serve Bridgeport, Chico, Boyd, Paradise, and all rural Wise County communities.' }
    ]
  },

  'wise-county-texas': {
    title: 'Wise County, Texas',
    slug: 'wise-county-texas',
    isGeoHub: true,
    description: 'Quality fencing, welding, concrete, and metal building services throughout Wise County, TX. Serving Decatur, Bridgeport, Chico, Boyd, Paradise, and surrounding ranch communities.',
    keywords: 'Wise County welding, Wise County fencing, Decatur fence, Bridgeport welding, Wise County concrete, Wise County metal buildings',
    image: '/images/hero-fence.jpg',
    longDescription: 'North Texas Welding and Fabrication provides comprehensive fencing, welding, concrete, and construction services across Wise County, Texas. From Decatur to Bridgeport, Chico to Boyd, and Paradise to all rural communities in between, our crew handles pipe fencing, ranch fencing, land clearing, metal buildings, concrete work, custom gates, and professional welding. Wise County\'s large agricultural properties and growing residential communities need a contractor who understands rural Texas work — that is exactly what we deliver.',
    benefits: [
      'Pipe fencing for ranches and residential properties',
      'Ranch fencing and livestock facilities',
      'Land clearing and forestry mulching',
      'Metal building and shop construction',
      'Concrete driveways, slabs, and foundations',
      'Custom gates and automated systems',
      'Mobile and shop welding services',
      'Serving 5+ Wise County communities'
    ],
    cities: ['Decatur', 'Bridgeport', 'Chico', 'Boyd', 'Paradise'],
    geoHubLinks: [
      { label: 'Decatur Services', url: '/services/decatur-texas' }
    ],
    faqs: [
      { question: 'What Wise County communities do you serve?', answer: 'We serve all of Wise County including Decatur, Bridgeport, Chico, Boyd, Paradise, Alvord, Rhome, and surrounding rural communities.' },
      { question: 'What services do you offer in Wise County?', answer: 'Pipe fencing, ranch fencing, custom gates, land clearing, forestry mulching, metal building construction, concrete work, gravel driveways, dirt work, mobile welding, shop welding, and custom metal fabrication.' },
      { question: 'Are Wise County regulations different from Parker County?', answer: 'Wise County has its own building permit requirements and regulations. Agricultural fencing typically does not require permits, but metal buildings and structures may need approval. We are familiar with Wise County requirements and help with the permit process.' },
      { question: 'How long does it take to get started on a Wise County project?', answer: 'We can usually schedule a site consultation within 3-5 business days for Wise County properties and begin work within 2-3 weeks of signing, depending on project scope and our current schedule.' }
    ]
  },

  'hood-county-texas': {
    title: 'Hood County, Texas',
    slug: 'hood-county-texas',
    isGeoHub: true,
    description: 'Professional welding, fencing, concrete, and metal building services in Hood County, TX. Serving Granbury, Tolar, Lipan, Oak Trail Shores, and surrounding areas.',
    keywords: 'Hood County welding, Hood County fencing, Granbury fence, Hood County pipe fence, Hood County concrete, Hood County metal buildings',
    image: '/images/hero-fence.jpg',
    longDescription: 'North Texas Welding and Fabrication serves Hood County, Texas with professional pipe fencing, ranch fencing, custom gates, land clearing, metal building construction, concrete work, and welding services. From the lakefront properties of Granbury to the ranches and farms of Tolar, Lipan, and Oak Trail Shores, Hood County property owners trust our team for quality construction that stands up to the Texas elements. Our full-service approach means one contractor handles your entire project.',
    benefits: [
      'Pipe fence and ranch fencing installation',
      'Custom gates and automated entry systems',
      'Land clearing and site preparation',
      'Metal building and barn construction',
      'Concrete driveways, patios, and foundations',
      'Mobile welding for on-site repairs',
      'Shop welding and custom fabrication',
      'Serving all Hood County communities'
    ],
    cities: ['Granbury', 'Tolar', 'Lipan', 'Oak Trail Shores'],
    geoHubLinks: [
      { label: 'Granbury Services', url: '/services/granbury-texas' }
    ],
    faqs: [
      { question: 'What Hood County communities do you serve?', answer: 'We serve all of Hood County including Granbury, Tolar, Lipan, Oak Trail Shores, and surrounding rural areas.' },
      { question: 'What services are available in Hood County?', answer: 'Pipe fencing, ranch fencing, custom gates, land clearing, metal buildings, concrete work (driveways, patios, slabs), mobile welding, shop welding, and custom fabrication.' },
      { question: 'Do you handle Hood County building permits?', answer: 'Yes. Metal buildings and certain structures require Hood County building permits. We assist with determining requirements and handling the application process for your project.' },
      { question: 'How far is your shop from Hood County?', answer: 'Our Springtown shop is approximately 35-45 minutes from most Hood County locations. We regularly work throughout Hood County and schedule crews efficiently across the area.' }
    ]
  }
};

// Locations are merged into services above — the separate export below is kept for backward compatibility
export const locations = {};